import React from "react";
import { useList, useTranslate } from "@refinedev/core";
import { Badge, Tabs, Typography, theme } from "antd";
import { OrderStatus } from "../../../models/order";

const { Text } = Typography;

interface StatusCountProps {
    count: number;
}

const StatusCount: React.FC<StatusCountProps> = ({ count }) => {
    const { token } = theme.useToken();
    if (count === 0) {
        return null;
    }

    return (
        <Badge
            count={count}
            style={{
                marginLeft: '8px',
                backgroundColor: token.colorPrimaryBg,
                color: token.colorPrimary,
            }}
        />
    );
};

interface StatusTabsProps {
    onStatusChange: (status: string) => void;
    activeKey: string;
}

export const StatusTabs: React.FC<StatusTabsProps> = ({ onStatusChange, activeKey }) => {
    const _t = useTranslate();

    // Single useList call to fetch all orders
    const { data } = useList({
        resource: "orders",
        pagination: { pageSize: 1000 }, // Fetch a large number to get all orders
        queryOptions: {
            keepPreviousData: true,
        }
    });

    // Calculate counts for each status
    const calculateStatusCounts = () => {
        if (!data?.data) return { all: 0, submitted: 0, ongoing: 0, completed: 0, canceled: 0 };

        const orders = data.data;
        const counts = {
            all: orders.length,
            submitted: 0,
            ongoing: 0,
            completed: 0,
            canceled: 0
        };

        orders.forEach(order => {
            switch (order.status) {
                case OrderStatus.submitted:
                    counts.submitted++;
                    break;
                case OrderStatus.ongoing:
                case OrderStatus.sav: // Include sav status in ongoing count
                    counts.ongoing++;
                    break;
                case OrderStatus.completed:
                    counts.completed++;
                    break;
                case OrderStatus.canceled:
                    counts.canceled++;
                    break;
            }
        });

        return counts;
    };

    const statusCounts = calculateStatusCounts();

    const renderTab = (status?: OrderStatus, defaultText?: string, count?: number) => (
        <span>
            <Text>{defaultText || _t(`orders.fields.statusValues.${status}`, '')}</Text>
            <StatusCount count={count || 0} />
        </span>
    );

    const items = [
        {
            label: renderTab(undefined, _t('orders.fields.statusValues.all', 'Toutes'), statusCounts.all),
            key: 'all',
        },
        {
            label: renderTab(OrderStatus.submitted, _t('orders.fields.statusValues.submitted'), statusCounts.submitted),
            key: OrderStatus.submitted,
        },
        {
            label: renderTab(OrderStatus.ongoing, _t('orders.fields.statusValues.ongoing'), statusCounts.ongoing),
            key: OrderStatus.ongoing,
        },
        {
            label: renderTab(OrderStatus.completed, _t('orders.fields.statusValues.completed'), statusCounts.completed),
            key: OrderStatus.completed,
        },
        {
            label: renderTab(OrderStatus.canceled, _t('orders.fields.statusValues.canceled'), statusCounts.canceled),
            key: OrderStatus.canceled,
        }
    ];

    return (
        <Tabs
            activeKey={activeKey}
            onChange={onStatusChange}
            items={items}
        />
    );
}; 